/**
 * main.ts — entry point.
 *
 * Sets up:
 *   1. WebGPU device + canvas context (gpu/Device.ts)
 *   2. Managed RenderTarget (rendering/RenderTarget.ts)
 *   3. Shared LightingUniforms / PaletteUniforms (rendering/*Uniforms.ts)
 *   4. Camera + orbital controller (app/*)
 *   5. RenderPipeline orchestrator with passes:
 *        ClearPass → BlitPass
 *      Stage 1 will insert SkyPass, TerrainRenderPass, WaterRenderPass before BlitPass.
 *
 * The frame loop only does:
 *   - update camera aspect
 *   - sync state into uniforms
 *   - pipeline.encode(encoder, frameContext)
 *   - submit
 *
 * Nothing in this file knows about specific passes beyond construction.
 */

import { Camera } from "./Camera";
import { OrbitalCameraController } from "./CameraController";
import { defaultAppState } from "./State";
import { initGpu, showError } from "./gpu/Device";
import { BlitPass } from "./rendering/BlitPass";
import { ClearPass } from "./rendering/ClearPass";
import {
  LightingUniforms,
  deg2rad,
  srgbHexToLinear,
} from "./rendering/LightingUniforms";
import { PaletteUniforms } from "./rendering/PaletteUniforms";
import { RenderPipeline } from "./rendering/RenderPipeline";
import { ManagedRenderTarget } from "./rendering/RenderTarget";
import { ShadowUniforms } from "./rendering/ShadowUniforms";
import type { FrameContext } from "./rendering/RenderPass";
import { SkyPass } from "./passes/SkyPass";
import { ShadowPass } from "./passes/ShadowPass";
import { TerrainRenderPass } from "./passes/TerrainRenderPass";
import { RoadRenderPass } from "./passes/RoadRenderPass";
import { RailRenderPass } from "./passes/RailRenderPass";
import { FetchPass } from "./passes/FetchPass";
import { SWEPass } from "./passes/SWEPass";
import { WindFieldPass } from "./passes/WindFieldPass";
import { SSAOPass } from "./passes/SSAOPass";
import { SkyCubemapPass } from "./passes/SkyCubemapPass";
import { BoatRenderPass } from "./passes/BoatRenderPass";
import { StaticMeshPass } from "./passes/StaticMeshPass";
import { loadBoatGlbScene } from "./assets/GltfLoader";
import {
  PlayerFenders,
  buildFenderHangersFromAnchors,
  buildTopsidesProfileFromScene,
} from "./sim/PlayerFenders";
import { WorldDecorationsBuilder } from "./sim/WorldDecorationsBuilder";
import type { DockFootprint } from "./sim/building-extrude";
import { buildMainDock } from "./sim/MainDockBuilder";
import { buildAnnotationDocks } from "./sim/AnnotationDocksBuilder";
import { AnnotationPowerlinesBuilder } from "./sim/AnnotationPowerlinesBuilder";
import { AnnotationEmbankmentsBuilder } from "./sim/AnnotationEmbankmentsBuilder";
import { WorldObjectBridgesBuilder } from "./sim/WorldObjectBridgesBuilder";
import { AnnotationWaterSignsBuilder } from "./sim/AnnotationWaterSignsBuilder";
import { AnnotationScatterPatchesBuilder } from "./sim/AnnotationScatterPatchesBuilder";
import { buildFloatingObjects } from "./sim/FloatingObjectsBuilder";
import { MooredBoatsBuilder } from "./sim/MooredBoatsBuilder";
import { RippleBufferPass } from "./passes/RippleBufferPass";
import { Boat } from "./sim/Boat";
import { Buoy } from "./sim/Buoy";
import { pickNamedAnchor } from "./sim/NamedAnchors";
import { BuoyLibrary, type BuoyKind } from "./sim/BuoyLibrary";
import {
  BuoyMeshRegistry,
  pushBuoysAwayFromBoats,
} from "./sim/BuoyMeshRegistry";
import { type MooringAnchor } from "./sim/Dock";
import { MooringRopeRenderer } from "./rendering/MooringRopeRenderer";
import { BoatLibrary } from "./sim/BoatLibrary";
import {
  findNearestWaterCell,
  sampleTerrainHeight,
  sampleTerrainSurface,
  type TerrainGameplayZone,
} from "./assets/TerrainAsset";
import { AIBoatPilot } from "./sim/AIBoatPilot";
import { assertBoatCleatMarkers } from "./sim/BoatCleats";
import {
  assertBoatCollisionLayerMarkers,
  getBoatCollisionLayers,
  getBoatCollisionWorldOutline,
  resolveCollisions,
  resolveTerrainShoreCollisions,
  type Aabb,
  type CollisionBody,
  type ImpactEvent,
  type Obb,
  obbOutline2D,
  obbYRange,
} from "./sim/Collisions";
import { WaterField } from "./sim/WaterField";
import { ParticleSystem } from "./sim/ParticleSystem";
import { BoatSpawner } from "./sim/BoatSpawner";
import { WakeTrailPass } from "./passes/WakeTrailPass";
import { WindStreakSpawner } from "./sim/WindStreakSpawner";
import { ParticlePass } from "./passes/ParticlePass";
import { BirdsSpawner } from "./sim/BirdsSpawner";
import { BirdsPass } from "./passes/BirdsPass";
import { BoatInput } from "./BoatInput";
import { ChaseCamera } from "./ChaseCamera";
import { collectFlybyPaths, FlybyCamera } from "./FlybyCamera";
import { createBoatHud } from "./BoatHud";
import { loadRuntimeTerrain } from "./data/terrain-runtime";
import { OceanSimulation } from "./passes/ocean/OceanSimulation";
import { WaterRenderPass } from "./passes/WaterRenderPass";
import type { AppState } from "./State";

/**
 * Live boat + environment readout for HUD widgets — re-exported from
 * {@link computeBoatTelemetry} so consumers don't have to reach into
 * the sim subfolder.
 */
export type { BoatTelemetry } from "./sim/BoatTelemetry";
import { computeBoatTelemetry, type BoatTelemetry } from "./sim/BoatTelemetry";

/**
 * Player-boat impact event surfaced to UI / audio — re-exported from
 * {@link PlayerImpactBus}.
 */
export type { BoatImpactEvent } from "./sim/PlayerImpactBus";
import { PlayerImpactBus, type BoatImpactEvent } from "./sim/PlayerImpactBus";

/**
 * Re-exports for {@link MooringPickupService} consumers (UI). The
 * service itself owns the policy (which cleats are eligible, the
 * boat-hook reach radius, the rest-length heuristic).
 */
import {
  MooringPickupService,
  BOAT_HOOK_REACH_M,
  type MooringPickupCandidate,
} from "./sim/MooringPickupService";
export { BOAT_HOOK_REACH_M };
export type { MooringPickupCandidate };
import { PlayerMooringStations } from "./sim/PlayerMooringStations";
import { AnchorPicker, type AnchorPick } from "./sim/AnchorPicker";
import { DeployedAnchor, PLAYER_ANCHOR_ID } from "./sim/DeployedAnchor";
export type { AnchorPick };

/**
 * Public handle returned by {@link startApp}. Use {@link CastOffProApp} for
 * the higher-level lifecycle wrapper.
 */
export interface AppHandle {
  /** Live, mutable simulation state. Changes are picked up next frame. */
  readonly state: AppState;
  /** Pause the rAF loop without releasing GPU resources. */
  stop(): void;
  /** Resume the rAF loop after a `stop()`. */
  resume(): void;
  /** Apply a partial patch to the live state. */
  setState(patch: Partial<AppState>): void;
  /** Available player boat presets discovered at startup. */
  listBoatChoices(): Array<{ id: string; name: string }>;
  /** Override player helm / throttle from an external UI. */
  setBoatControls(patch: {
    /** Legacy combined throttle in [-1, 1]. Decomposed into gas + gear. */
    throttle?: number | null;
    /** Gas grip in [0, 1] — independent from the gear lever. */
    gas?: number | null;
    /** Gear lever: -1 = reverse, 0 = neutral, +1 = forward. */
    gear?: -1 | 0 | 1 | null;
    steer?: number | null;
    engineSteer?: number | null;
  }): void;
  /**
   * Read the current player boat state for HUD widgets. Returns null
   * until the first frame has run (boat instance not yet attached).
   *
   * All angles are radians (compass-style, 0 = north, +X = east, CCW
   * from above); speeds in m/s.
   */
  boatTelemetry(): BoatTelemetry | null;
  /**
   * Drain and return impact events recorded since the last call. Each
   * call returns an independent array; an idle consumer cannot leak
   * memory (the queue is hard-capped on the producer side).
   */
  consumeImpactEvents(): BoatImpactEvent[];
  /**
   * Find the closest mooring anchor within boat-hook range of the
   * given cleat's fairlead. Only bow / stern_port / stern_starboard
   * cleats can be used to pick up a mooring with a boat hook; midship
   * cleats always return null. Returns null if no anchor is in reach.
   */
  getMooringPickupCandidate(cleatName: string): MooringPickupCandidate | null;
  /**
   * Pick the closest mooring anchor under the given normalised device
   * coordinates (NDC — both axes in [-1, +1], +Y up). Returns null if
   * the click missed every registered anchor (bollards + buoys).
   * Used by the in-world contextual menu ("tap a poller, choose a
   * line to make fast").
   */
  pickAnchorAtNdc(ndcX: number, ndcY: number): AnchorPick | null;
  /**
   * Current world-space distance (m) from the named cleat's fairlead
   * to its currently-attached mooring anchor. Returns null when the
   * line is loose / not bound to anything. Used by the “Wybierz /
   * Luzuj” UI so the crew can shorten or lengthen the rope by a
   * known amount relative to the current taut length.
   */
  getFairleadAnchorDistance(cleatName: string): number | null;
  /**
   * Read the player boat's freeboard (deck-edge height above the
   * waterline, metres). Crew-action timings scale off this — picking
   * up a low-floating buoy from a high-freeboard bow takes longer.
   */
  getPlayerFreeboard(): number;
  /** Read player boat anchor-related equipment limits/capabilities. */
  getPlayerAnchorEquipment(): {
    hasDepthSounder: boolean;
    anchorChainMaxLength: number;
    anchorRodeType: import("./sim/BoatHull").AnchorRodeType;
    anchorRodeMaxLength: number;
    anchorRodeWeightKgPerM: number;
  };
  /** True if the player boat's mast can be lowered on a tabernacle. */
  getPlayerHasFoldableMast(): boolean;
  /**
   * Set the player boat's desired mast angle (rad). 0 = stepped
   * (vertical), π/2 ≈ laid aft. The hull slews `mastAngle` toward
   * the target across multiple frames so the change is visible.
   * No-op when the player hull is not `foldableMast`.
   */
  setPlayerMastAngleTarget(angleRad: number): void;
  /**
   * Toggle the player boat's boom detachment flag. When `true`,
   * the renderer hides any scene part whose name starts with `boom`
   * — used by the crew checklist to remove the boom before the
   * mast is laid down on a tabernacle. No-op when the player hull
   * is not `foldableMast`.
   */
  setPlayerBoomDetached(detached: boolean): void;
  /** Available terrain presets discovered at startup. */
  listTerrainPresets(): Array<{ id: string; name: string }>;
  /**
   * Trigger an immediate bird flyover (small V-flock) around the player.
   * No-op when the 3-flight cap is already reached. Does not require
   * `state.showBirds` to be on — but the flock won't render while it's off.
   */
  releaseBirds(): void;
  /**
   * Spawn one buoy at the given world XZ. Used by gamification /
   * exercise scenarios to drop course marks (cardinal, lateral) or
   * extra mooring floats into a live world. Returns the new buoy or
   * `null` when the id collides with an existing entity.
   *
   * `kind` must match one of the entries in `public/buoys/index.json`;
   * the entry's defaults supply radius / mass / anchored unless the
   * caller overrides via `opts`.
   */
  spawnBuoy(
    kind: import("./sim/BuoyLibrary").BuoyKind,
    posX: number,
    posZ: number,
    opts?: {
      id?: string;
      anchored?: boolean;
      headingY?: number;
    },
  ): import("./sim/Buoy").Buoy | null;
  /** Stop the loop and release all GPU + DOM resources. */
  dispose(): void;
  /**
   * DEV-only escape hatch — undefined in prod builds. Exposes raw
   * simulation objects (player boat, buoys, docks) for manual tests
   * driven from the integrated browser harness.
   */
  _devInternals?: {
    playerBoat: import("./sim/Boat").Boat;
    buoys: import("./sim/Buoy").Buoy[];
    annotationDocks: import("./sim/Dock").Dock[];
    mainDock: import("./sim/Dock").Dock;
    chaseCamera: import("./ChaseCamera").ChaseCamera;
    camera: import("./Camera").Camera;
  };
}

export interface AppOpts {
  /** Optional initial state overrides applied before the first frame. */
  initialState?: Partial<AppState>;
  /** Render terrain/water/decorations only for the new-game background. */
  backgroundPreview?: boolean;
  /** Render the in-canvas boat HUD. Default true. */
  enableHud?: boolean;
}

function bodyPointToWorld3(
  boat: Boat,
  point: readonly [number, number, number],
): [number, number, number] {
  const [x, z] = boat.bodyToWorld(point[0], point[1], point[2]);
  return [x, boat.posY + point[1], z];
}

function centroid2(
  points: ReadonlyArray<readonly [number, number]>,
): [number, number] {
  if (points.length === 0) return [0, 0];
  let x = 0;
  let z = 0;
  for (const point of points) {
    x += point[0];
    z += point[1];
  }
  return [x / points.length, z / points.length];
}

function gameplayZoneCenter(zone: TerrainGameplayZone): [number, number] {
  if (zone.geometry.type === "point") return zone.geometry.point;
  return centroid2(zone.geometry.points);
}

async function startApp(
  canvas: HTMLCanvasElement,
  opts: AppOpts = {},
): Promise<AppHandle> {
  const gpu = await initGpu(canvas);
  const backgroundPreview = opts.backgroundPreview === true;

  // --- Shared infrastructure -------------------------------------------------
  const rt = new ManagedRenderTarget(
    gpu.device,
    gpu.size.width,
    gpu.size.height,
  );
  gpu.onResize((w, h) => rt.resize(w, h));

  const palette = new PaletteUniforms(gpu.device);
  const lighting = new LightingUniforms(gpu.device);
  lighting.state.fogColor = srgbHexToLinear("#A7B4AE");
  lighting.upload();

  const camera = new Camera(gpu.device, {
    initialDistance: backgroundPreview ? 120 : 80,
    initialAzimuth: deg2rad(225),
    initialElevation: deg2rad(backgroundPreview ? 34 : 15),
    target: [0, 0, 0],
    far: 8000,
  });
  camera.setAspect(gpu.size.width / gpu.size.height);
  gpu.onResize((w, h) => camera.setAspect(w / h));
  new OrbitalCameraController(canvas, camera);

  // --- App state -------------------------------------------------------------
  // Storage keys mirror `src/app/core/storage/storage-keys.ts` (source of truth).
  // We can't import from `app/` here without breaking sim-core's independence,
  // so the strings are duplicated locally and the migrator in `app/main.ts`
  // ensures legacy `vf.*` values have already been copied across by the time
  // we reach this point.
  const state = defaultAppState();
  // Restore previous player-boat selection from localStorage if present.
  const savedBoatId = localStorage.getItem("castoffpro.playerBoatId");
  if (savedBoatId) state.playerBoatId = savedBoatId;
  // Restore previous terrain selection from localStorage if present.
  const savedTerrainId = localStorage.getItem("castoffpro.terrainId");
  if (savedTerrainId) state.terrainId = savedTerrainId;
  // Restore previous render-quality selection from localStorage if present.
  const savedRenderQuality = localStorage.getItem("castoffpro.renderQuality");
  if (
    savedRenderQuality === "low" ||
    savedRenderQuality === "medium" ||
    savedRenderQuality === "high"
  ) {
    state.renderQuality = savedRenderQuality;
  }
  // Apply caller-supplied overrides last so they win over persisted prefs.
  if (opts.initialState) Object.assign(state, opts.initialState);

  // Quality profile: shadow-map resolution + SSAO toggle. Picked here so the
  // values are baked into the pass constructors below (changing the preset
  // at runtime would require recreating shadow textures + consumer bind
  // groups in every consumer pass — not worth the complexity, the UI does
  // a `location.reload()` on change instead).
  const qualityProfile = {
    low: {
      shadowResolution: 1024,
      ssaoEnabled: false,
      ssaoIntensity: 1.1,
      terrainQuads: 512,
    },
    medium: {
      shadowResolution: 1024,
      ssaoEnabled: false,
      ssaoIntensity: 1.1,
      terrainQuads: 768,
    },
    high: {
      shadowResolution: 4096,
      ssaoEnabled: true,
      ssaoIntensity: 1.3,
      terrainQuads: 1024,
    },
  }[state.renderQuality];

  // --- Pipeline (orchestrator pattern) --------------------------------------
  const pipeline = new RenderPipeline();
  // ClearPass clears depth (reverse-Z → 0.0) and color (overwritten by SkyPass).
  pipeline.add(new ClearPass(rt, [0, 0, 0]));
  pipeline.add(new SkyPass(gpu.device, rt, camera, lighting, palette));

  // Shadow infrastructure — created BEFORE terrain so ShadowPass can hold a
  // ref, and so terrain/water shaders receive the consumer bind-group layout.
  const shadow = new ShadowUniforms(gpu.device, {
    resolution: qualityProfile.shadowResolution,
  });
  // Shared consumer-side layout: terrain pipeline, water pipeline, and
  // ShadowPass's consumer bind group all use this exact instance.
  const shadowConsumerLayout = gpu.device.createBindGroupLayout({
    label: "ShadowConsumer.bgl",
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
        buffer: { type: "uniform" },
      },
      {
        binding: 1,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: "depth" },
      },
      {
        binding: 2,
        visibility: GPUShaderStage.FRAGMENT,
        sampler: { type: "comparison" },
      },
    ],
  });
  const runtimeTerrain = await loadRuntimeTerrain({
    search: window.location.search,
    requestedTerrainId: state.terrainId,
    state,
    palette,
  });
  const terrainPresets = runtimeTerrain.presets;
  const demHeightmap = runtimeTerrain.heightmap;
  const terrainClassification = runtimeTerrain.classification;
  const terrainRoadCoverage = runtimeTerrain.roadCoverage;
  const terrainPolygonFade = runtimeTerrain.polygonFade;
  const activeTerrain = runtimeTerrain.asset;

  const terrainPass = new TerrainRenderPass(
    gpu.device,
    rt,
    camera,
    lighting,
    palette,
    shadowConsumerLayout,
    {
      waterLevel: state.waterLevel,
      heightmap: demHeightmap,
      classification: terrainClassification,
      roadCoverage: terrainRoadCoverage,
      polygonFade: terrainPolygonFade,
      quads: qualityProfile.terrainQuads,
    },
  );
  const shadowPass = new ShadowPass(
    gpu.device,
    terrainPass,
    lighting,
    shadow,
    shadowConsumerLayout,
  );
  // Add ShadowPass right after ClearPass (depth-only off-screen render).
  pipeline.insertBefore("SkyPass", shadowPass);
  pipeline.add(terrainPass);

  // RoadRenderPass is constructed here so we can hold a reference to insert
  // it after WaterRenderPass below (bridges over water must overpaint the
  // water surface; rendering before water hid the deck underneath).
  let roadPass: RoadRenderPass | null = null;
  let railPass: RailRenderPass | null = null;
  if (activeTerrain) {
    const { loadRoadAsset } = await import("./assets/RoadAsset");
    const roadAsset = await loadRoadAsset(
      activeTerrain.baseUrl,
      activeTerrain.meta.roads ?? "terrain_roads.json",
    );
    if (roadAsset.roads.length > 0) {
      roadPass = new RoadRenderPass(gpu.device, rt, camera, lighting, {
        roads: roadAsset,
        terrain: activeTerrain,
        terrainPass,
      });
    }
    const { loadRailAsset } = await import("./assets/RailAsset");
    const railAsset = await loadRailAsset(
      activeTerrain.baseUrl,
      activeTerrain.meta.rails ?? "terrain_rails.json",
    );
    if (railAsset.rails.length > 0) {
      railPass = new RailRenderPass(gpu.device, rt, camera, lighting, {
        rails: railAsset,
        terrain: activeTerrain,
        terrainPass,
      });
    }
  }
  // Wind-fetch precompute (compute pass, runs only when wind/water dirty).
  const fetchPass = new FetchPass(gpu.device, terrainPass);
  pipeline.add(fetchPass);
  // Shallow-water seiches (basin-scale slow swell).
  const swePass = new SWEPass(gpu.device, terrainPass);
  pipeline.add(swePass);
  // Local wind-shelter field (sharp lee strips behind land).
  const windFieldPass = new WindFieldPass(gpu.device, terrainPass);
  pipeline.add(windFieldPass);
  // Dynamic reflection cubemap (sky + local shoreline silhouettes).
  const skyCubemapPass = new SkyCubemapPass(
    gpu.device,
    lighting,
    palette,
    terrainPass,
  );
  pipeline.add(skyCubemapPass);
  // Ocean simulation runs before water rendering (compute passes only).
  const ocean = new OceanSimulation(gpu.device);
  pipeline.add(ocean);
  // Load all boat designs from /public/boats/ at startup. Adding a new
  // boat type means dropping a JSON spec into that folder + an index entry,
  // no code change required.
  const boatLibrary = await BoatLibrary.load("boats/");

  // Boats — created here so RippleBufferPass can read their state.
  // Fall back to Antila 27 if the saved id no longer exists in the library.
  const playerBoatId = boatLibrary.ids().includes(state.playerBoatId)
    ? state.playerBoatId
    : "antila_27";
  state.playerBoatId = playerBoatId;
  // Player boat uses the animated (scene-mode) hull if available so the
  // wheel / boom / rudder can animate. Moored neighbours stay on the flat
  // single-mesh hull via `boatLibrary.get(...)` for draw-call efficiency.
  const playerBoat = new Boat(boatLibrary.getAnimated(playerBoatId));
  const playerSpawn = activeTerrain?.gameplayZones.find(
    (zone) => zone.type === "player_spawn",
  );
  const [spawnX, spawnZ] = playerSpawn
    ? gameplayZoneCenter(playerSpawn)
    : ([0, 0] as [number, number]);
  playerBoat.posX = spawnX;
  playerBoat.posZ = spawnZ;
  playerBoat.heading = playerSpawn?.headingY ?? -Math.PI * 0.5;
  // If a real-terrain preset is loaded, snap the player to the nearest water
  // cell so we don't start on land (the procedural marina layout is tuned for
  // the synthetic basin and may sit on shore in real DEMs).
  if (activeTerrain) {
    const snapped = findNearestWaterCell(
      activeTerrain,
      playerBoat.posX,
      playerBoat.posZ,
    );
    if (snapped) {
      playerBoat.posX = snapped[0];
      playerBoat.posZ = snapped[1];
      console.log(
        `[Spawn] player snapped to water at (${snapped[0].toFixed(1)}, ${snapped[1].toFixed(1)})`,
      );
    }
  }
  const boats: Boat[] = [playerBoat];
  const visibleBoats = backgroundPreview ? [] : boats;
  // AI fleet and marina mooring layouts are temporarily disabled — focusing
  // on the player boat in isolation. See git history for previous content.
  const aiPilots: AIBoatPilot[] = [];

  // L3 dock features will feed this list; empty terrain_objects.json means
  // no authored docks for the preset.
  const dockFootprints: DockFootprint[] = [];
  // Domyślnie wyłączone — skupiamy się na pierwszym poprawnym cumowaniu
  // gracza, więc inne jednostki w slipach tylko zaśmiecają scenę. Ustaw > 0
  // żeby z powrotem rozsadzić sąsiadów po dokach.
  const MAX_MOORED_BOATS = 0;
  const mooredBoats = new MooredBoatsBuilder({
    boats,
    boatLibrary,
    playerBoatId,
  });
  mooredBoats.spawn(dockFootprints, MAX_MOORED_BOATS);
  const MOORED_COUNT = mooredBoats.mooredCount;

  // Collision bodies: every boat participates. Moored ones are immovable so
  // they act as floating walls without drifting away from their slip.
  const collisionBodies: CollisionBody[] = boats.map((b, i) => ({
    boat: b,
    immovable: i >= boats.length - MOORED_COUNT,
    requireCollisionLayerMarkers: b === playerBoat,
  }));
  assertBoatCollisionLayerMarkers(playerBoat);
  assertBoatCleatMarkers(playerBoat.params);
  // Reused per-frame impact-event buffer; the bus filters down to
  // player-boat events and applies a per-kind cooldown before queuing
  // them for the consumer.
  const frameImpacts: ImpactEvent[] = [];
  const playerImpactBus = new PlayerImpactBus(playerBoat);
  // Boat-wake ripple buffer is retained as a zeroed texture binding for the
  // water pass, but not dispatched while ripple displacement is disabled.
  const ripplePass = new RippleBufferPass(
    gpu.device,
    terrainPass,
    visibleBoats,
  );
  // Persistent wake-foam RTT — painted by tracing the boat's stern path each
  // frame, sampled by the water shader as a foam mask.
  const wakeTrailPass = new WakeTrailPass(gpu.device, visibleBoats);
  pipeline.add(wakeTrailPass);
  const waterPass = new WaterRenderPass(
    gpu.device,
    rt,
    camera,
    lighting,
    palette,
    terrainPass,
    ocean,
    shadowPass.consumerLayout,
    fetchPass,
    swePass,
    windFieldPass,
    skyCubemapPass,
    ripplePass,
    wakeTrailPass,
    visibleBoats,
  );
  pipeline.add(waterPass);
  // (RoadRenderPass is added after staticMeshPass below so trees, water,
  // and dock/buoy meshes can't overpaint the bridge deck.)
  // SSAO runs after all geometry has populated the depth buffer.
  const boatPass = new BoatRenderPass(
    gpu.device,
    rt,
    camera,
    lighting,
    visibleBoats,
    shadowConsumerLayout,
    shadow,
  );
  pipeline.add(boatPass);

  // Spray + wake particles.
  const particleSystem = new ParticleSystem(2048);
  const boatSpawner = new BoatSpawner(particleSystem, playerBoat);
  const windStreakSpawner = new WindStreakSpawner(particleSystem);
  const particlePass = new ParticlePass(
    gpu.device,
    rt,
    camera,
    lighting,
    particleSystem,
  );
  pipeline.add(particlePass);

  // Occasional bird flyovers (single gulls / small V-flocks).
  const birdsSpawner = new BirdsSpawner();
  const birdsPass = new BirdsPass(
    gpu.device,
    rt,
    camera,
    lighting,
    birdsSpawner,
  );
  pipeline.add(birdsPass);

  // Static / instanced meshes (Stage 5: dock, lighthouse, buoys, trees).
  const staticMeshPass = new StaticMeshPass(
    gpu.device,
    rt,
    camera,
    lighting,
    shadowConsumerLayout,
    shadow,
  );
  pipeline.add(staticMeshPass);

  // Road ribbons render AFTER water + decorations so the bridge deck
  // overpaints everything geometrically behind it (z-bias keeps it crisp).
  if (roadPass) pipeline.add(roadPass);
  if (railPass) pipeline.add(railPass);

  // ---- Marina dock --------------------------------------------------------
  // Marina dock geometry is temporarily disabled — focusing on the player
  // boat in isolation. See git history for the previous content.
  const dockAabbs: Aabb[] = [];
  // Yaw-rotated colliders for procedural obstacles whose local frame is
  // not aligned with world XZ (canal embankments, slanted jetties, …).
  // Fed through the same `resolveCollisions` call as AABBs.
  const dockObbs: Obb[] = [];

  // ---- Compiled L2 structures --------------------------------------------
  if (activeTerrain) {
    new AnnotationPowerlinesBuilder(staticMeshPass).build(
      activeTerrain,
      state.waterLevel,
      dockAabbs,
    );
    new AnnotationEmbankmentsBuilder(staticMeshPass).build(
      activeTerrain,
      state.waterLevel,
      dockAabbs,
      dockObbs,
    );
    await new WorldObjectBridgesBuilder(staticMeshPass).build(
      activeTerrain,
      state.waterLevel,
      dockAabbs,
    );
    new AnnotationWaterSignsBuilder(staticMeshPass).build(activeTerrain);
    await new AnnotationScatterPatchesBuilder(staticMeshPass).build(
      activeTerrain,
      state.waterLevel,
    );
  }

  // ---- Trees / buildings from compiled terrain semantics -----------------
  if (activeTerrain) {
    await new WorldDecorationsBuilder(staticMeshPass).build(activeTerrain);
  }

  // ---- Buoys (mooring marks + navigation aids) ---------------------------
  // GLB models per kind come from `public/buoys/index.json`; the
  // `BuoyMeshRegistry` lazily registers one StaticMeshPass group per
  // kind and falls back to a procedural sphere when the GLB is missing
  // so the world stays alive during asset turnover.
  const buoys: Buoy[] = [];
  let buoyLibrary: BuoyLibrary;
  try {
    buoyLibrary = await BuoyLibrary.load();
  } catch (err) {
    console.warn(
      "[Buoys] failed to load buoy manifest; procedural fallback only",
      err,
    );
    buoyLibrary = BuoyLibrary.empty();
  }
  const buoyMeshRegistry = new BuoyMeshRegistry(staticMeshPass, buoyLibrary);
  // Preload every known buoy kind so subsequent `spawnBuoy` calls
  // (incl. runtime / gamification spawns) are synchronous and the
  // visuals appear on the same frame.
  await Promise.all(
    buoyLibrary.ids().map((k) => buoyMeshRegistry.ensureKind(k)),
  );

  /**
   * Spawn a buoy at world XZ. Returns the new {@link Buoy} or `null`
   * when the id is already in use (idempotent retries from gamification
   * shouldn't double-spawn).
   */
  function spawnBuoy(req: {
    kind: BuoyKind;
    posX: number;
    posZ: number;
    id: string;
    anchored?: boolean;
    headingY?: number;
  }): Buoy | null {
    if (buoys.some((b) => b.id === req.id)) return null;
    const entry = buoyLibrary.tryGet(req.kind);
    const buoy = new Buoy({
      posX: req.posX,
      posZ: req.posZ,
      id: req.id,
      kind: req.kind,
      anchored: req.anchored ?? entry?.anchored ?? true,
      headingY: req.headingY ?? 0,
      radiusXZ: entry?.radiusXZ,
      radiusY: entry?.radiusY,
      mass: entry?.mass,
    });
    buoys.push(buoy);
    buoyMeshRegistry.addInstance(buoy);
    return buoy;
  }

  // ---- Dock (pile-supported wooden pier) ---------------------------------
  // Manual dock spawn lives behind a flag inside MainDockBuilder.
  // The Dock instance is always built so the mooring UI / stations
  // / pickup service have anchor objects to bind to even when
  // annotation docks aren't present.
  const { dock } = buildMainDock({
    playerBoat,
    staticMeshPass,
    dockAabbs,
    waterLevel: state.waterLevel,
  });

  // ---- Authored dock features --------------------------------------------
  const { docks: annotationDocks } = buildAnnotationDocks({
    dockFootprints,
    staticMeshPass,
    spawnBuoy,
    dockAabbs,
    waterLevel: state.waterLevel,
    terrain: activeTerrain,
  });

  // ---- L3 buoy objects ---------------------------------------------------
  if (activeTerrain) {
    let placedMarkers = 0;
    for (const object of activeTerrain.worldObjects) {
      if (object.type !== "buoy") continue;
      const spawned = spawnBuoy({
        kind: object.kind,
        posX: object.position[0],
        posZ: object.position[1],
        id: object.id,
        anchored: object.anchored,
        headingY: object.headingY,
      });
      if (spawned) placedMarkers++;
    }
    if (placedMarkers > 0) {
      console.log(`[Buoys] placed ${placedMarkers} L3 buoy object(s)`);
    }
  }

  // ---- Static floating objects ------------------------------------------
  // Simple harbour occupants loaded from public/floating-objects/index.json.
  // They render as instanced GLBs and contribute static AABBs to player-boat
  // collision, but they are not Boat instances and do not use player-boat
  // physics, anchors, markers or moving parts.
  const floatingObjects = await buildFloatingObjects({
    staticMeshPass,
    dockFootprints,
    staticAabbs: dockAabbs,
    waterLevel: state.waterLevel,
    terrain: activeTerrain,
  });

  // ---- Mooring stations (one per boat cleat) -----------------------------
  // Each station is "wired up" but its anchor target is read each frame from
  // `state.mooringTargets[cleatName]`. `null` → line is loose (no force, no
  // rope rendered). Operator picks a bollard from the UI dropdown.
  const allAnchors: Map<string, MooringAnchor> = new Map();
  for (const b of dock.bollards) allAnchors.set(b.anchorId, b);
  for (const ad of annotationDocks) {
    for (const b of ad.bollards) allAnchors.set(b.anchorId, b);
  }
  // Buoys (incl. those spawned next to buoy-style docks) are also valid
  // mooring anchors — register them so the UI dropdown sees them.
  // Navigation marks (lateral / cardinal) are NOT registered as mooring
  // anchors; they exist only to be looked at + collided with.
  const isMooringBuoy = (b: Buoy): boolean => {
    const entry = buoyLibrary.tryGet(b.kind);
    return entry?.category === "mooring" || entry === undefined;
  };
  for (const buoy of buoys) {
    if (isMooringBuoy(buoy)) allAnchors.set(buoy.anchorId, buoy);
  }

  // Boat-hook pickup queries live behind a dedicated service so the
  // policy ("which cleats can reach a hook, what counts as in range,
  // how long should the resulting rope be") is testable in isolation.
  const pickupService = new MooringPickupService(playerBoat);
  for (const b of dock.bollards) pickupService.registerAnchor(b, "bollard");
  for (const ad of annotationDocks) {
    for (const b of ad.bollards) pickupService.registerAnchor(b, "bollard");
  }
  for (const buoy of buoys) {
    if (isMooringBuoy(buoy)) pickupService.registerAnchor(buoy, "buoy");
  }

  // World-space anchor picker for in-world taps. Same anchor set as
  // the pickup service; lives separately because the policy differs
  // (any cleat can target any anchor; pickup is fairlead-radius bound).
  const anchorPicker = new AnchorPicker(camera);
  for (const b of dock.bollards) anchorPicker.registerAnchor(b, "bollard");
  for (const ad of annotationDocks) {
    for (const b of ad.bollards) anchorPicker.registerAnchor(b, "bollard");
  }
  for (const buoy of buoys) {
    if (isMooringBuoy(buoy)) anchorPicker.registerAnchor(buoy, "buoy");
  }

  const ROPE_SEGMENTS = 16;

  // Reusable rope renderer — one mesh group, every line claims a fixed
  // slice of segments. Player + AI/moored boats all share this instance.
  const ropeRenderer = new MooringRopeRenderer(staticMeshPass, {
    segmentsPerLine: ROPE_SEGMENTS,
    meshId: "mooring_rope",
    color: [0.92, 0.85, 0.55], // sun-bleached hemp
    radius: 0.012,
  });
  // Separate renderer for chain rode — darker, thicker, looks like
  // galvanized steel links from a distance.
  const chainRenderer = new MooringRopeRenderer(staticMeshPass, {
    segmentsPerLine: ROPE_SEGMENTS,
    meshId: "mooring_chain",
    color: [0.32, 0.34, 0.36],
    radius: 0.018,
  });

  // Short thin lines that hang each player fender from its hanger point.
  // Few segments are enough — the rope is short and held taut by the
  // distance constraint, so we mostly need a straight visual.
  const fenderRopeRenderer = new MooringRopeRenderer(staticMeshPass, {
    segmentsPerLine: 4,
    meshId: "fender_rope",
    color: [0.72, 0.84, 0.92],
    radius: 0.006,
    sagGain: 0,
  });

  // Debug overlay: a thin red polyline drawn at the water surface along
  // the player boat's waterline collision outline — same polygon that
  // shore/dock tests use. Toggled by `state.showCollisionFootprint`.
  const collisionFootprintRenderer = new MooringRopeRenderer(staticMeshPass, {
    segmentsPerLine: 96,
    meshId: "collision_footprint",
    color: [1.0, 0.15, 0.15],
    radius: 0.04,
    sagGain: 0,
  });
  const collisionFootprintHandle = collisionFootprintRenderer.addLine();
  collisionFootprintRenderer.hideLine(collisionFootprintHandle);

  // Debug overlay: wireframe of every static collision AABB (docks,
  // embankments, powerlines, bridges, floating objects, authored
  // `UBX_*` colliders ...). Built once now that `dockAabbs` is fully
  // populated. Each AABB contributes 12 short tube segments — one per
  // box edge. Toggled by `state.showStaticColliders`.
  const colliderEdgesRenderer = new MooringRopeRenderer(staticMeshPass, {
    segmentsPerLine: 1,
    meshId: "static_collider_edges_debug",
    color: [0.2, 1.0, 0.4],
    radius: 0.05,
    sagGain: 0,
    radial: 6,
  });
  type AabbEdgeCache = {
    handles: number[];
    corners: Array<[number, number, number]>;
  };
  const colliderEdgeCache: AabbEdgeCache[] = [];
  const AABB_EDGE_PAIRS: ReadonlyArray<readonly [number, number]> = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ];
  for (const aabb of dockAabbs) {
    const { min, max } = aabb;
    const corners: Array<[number, number, number]> = [
      [min[0], min[1], min[2]],
      [max[0], min[1], min[2]],
      [max[0], min[1], max[2]],
      [min[0], min[1], max[2]],
      [min[0], max[1], min[2]],
      [max[0], max[1], min[2]],
      [max[0], max[1], max[2]],
      [min[0], max[1], max[2]],
    ];
    const handles: number[] = [];
    for (let i = 0; i < AABB_EDGE_PAIRS.length; i++) {
      const h = colliderEdgesRenderer.addLine();
      colliderEdgesRenderer.hideLine(h);
      handles.push(h);
    }
    colliderEdgeCache.push({ handles, corners });
  }
  // OBB wireframes: the 8 corners are the rotated XZ outline lifted to
  // the box's Y range. Same 12 edges as AABB so the renderer/cache stays
  // uniform.
  for (const obb of dockObbs) {
    const outline = obbOutline2D(obb);
    const yr = obbYRange(obb);
    const corners: Array<[number, number, number]> = [
      [outline[0]!.x, yr.min, outline[0]!.z],
      [outline[1]!.x, yr.min, outline[1]!.z],
      [outline[2]!.x, yr.min, outline[2]!.z],
      [outline[3]!.x, yr.min, outline[3]!.z],
      [outline[0]!.x, yr.max, outline[0]!.z],
      [outline[1]!.x, yr.max, outline[1]!.z],
      [outline[2]!.x, yr.max, outline[2]!.z],
      [outline[3]!.x, yr.max, outline[3]!.z],
    ];
    const handles: number[] = [];
    for (let i = 0; i < AABB_EDGE_PAIRS.length; i++) {
      const h = colliderEdgesRenderer.addLine();
      colliderEdgesRenderer.hideLine(h);
      handles.push(h);
    }
    colliderEdgeCache.push({ handles, corners });
  }
  let colliderWireframesVisible = false;
  const setColliderWireframesVisible = (visible: boolean): void => {
    if (visible === colliderWireframesVisible) return;
    if (visible) {
      for (const entry of colliderEdgeCache) {
        for (let i = 0; i < AABB_EDGE_PAIRS.length; i++) {
          const [a, b] = AABB_EDGE_PAIRS[i]!;
          colliderEdgesRenderer.updatePolyline(entry.handles[i]!, [
            entry.corners[a]!,
            entry.corners[b]!,
          ]);
        }
      }
    } else {
      for (const entry of colliderEdgeCache) {
        for (const h of entry.handles) colliderEdgesRenderer.hideLine(h);
      }
    }
    staticMeshPass.markDirty(colliderEdgesRenderer.meshId);
    colliderWireframesVisible = visible;
  };

  // Player-boat mooring stations: one MooringLine per cleat, with its
  // own rope-renderer slot. The class encapsulates configuration,
  // per-frame physics and per-frame rope geometry updates.
  const playerStations = new PlayerMooringStations({
    playerBoat,
    ropeRenderer,
    chainRenderer,
    pickupService,
    allAnchors,
    initialAnchor: dock.bollards[0]!,
  });

  // 5 cleats supported in the UI: bow, stern (port + starboard), midship
  // (port + starboard). Cleat coordinates come only from required GLB markers.
  const loa = playerBoat.params.length;
  const anchorRodeExit = pickNamedAnchor(playerBoat.params.namedAnchors, [
    "Anchor.RodeExit",
    "anchor.rodeexit",
    "Anchor_RodeExit",
    "anchor_rode_exit",
    "AnchorRodeExit",
  ]);
  const anchorRodeAttach = pickNamedAnchor(playerBoat.params.namedAnchors, [
    "Anchor.RodeAttach",
    "anchor.rodeattach",
    "Anchor_RodeAttach",
    "anchor_rode_attach",
    "AnchorRodeAttach",
  ]);
  const anchorPartPivot = playerBoat.params.customScene?.parts.find((p) =>
    /^anchor$/i.test(p.name),
  )?.pivotPosition;
  playerStations.addStation("bow", -0.05, +0.15, anchorRodeExit);
  playerStations.addStation("stern_port", +0.05, +0.15);
  playerStations.addStation("stern_starboard", +0.05, +0.15);
  playerStations.addStation("midship_port", 0, +0.05);
  playerStations.addStation("midship_starboard", 0, +0.05);

  // Allocate one slot in each renderer; we render in whichever style
  // matches the boat's anchor rode type and hide the other.
  const deckAnchorRodeRopeHandle = ropeRenderer.addLine();
  const deckAnchorRodeChainHandle = chainRenderer.addLine();

  const animatedAnchorRodeAttachBody = (): [number, number, number] | null => {
    if (!anchorRodeAttach) return null;
    const dropY = -Math.max(0, Math.min(1, playerBoat.anchorDeployT)) * 2.8;
    if (!anchorPartPivot)
      return [
        anchorRodeAttach[0],
        anchorRodeAttach[1] + dropY,
        anchorRodeAttach[2],
      ];
    const a = -playerBoat.anchorDeployT * Math.PI * 0.65;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    const dx = anchorRodeAttach[0] - anchorPartPivot[0];
    const dy = anchorRodeAttach[1] - anchorPartPivot[1];
    const dz = anchorRodeAttach[2] - anchorPartPivot[2];
    return [
      anchorPartPivot[0] + dx * ca - dy * sa,
      anchorPartPivot[1] + dx * sa + dy * ca + dropY,
      anchorPartPivot[2] + dz,
    ];
  };

  const updateDeckAnchorRodeVisual = (): void => {
    const attachBody = animatedAnchorRodeAttachBody();
    const exitBody = anchorRodeExit ?? ([loa * 0.45, 0.95, 0] as const);
    const useChain =
      playerBoat.params.anchorRodeType === "chain" ||
      playerBoat.params.anchorRodeType === "mixed";
    if (state.anchorState !== "dropping" || !attachBody) {
      ropeRenderer.hideLine(deckAnchorRodeRopeHandle);
      chainRenderer.hideLine(deckAnchorRodeChainHandle);
      return;
    }
    const a = bodyPointToWorld3(playerBoat, exitBody);
    const b = bodyPointToWorld3(playerBoat, attachBody);
    const dist = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
    const active = useChain ? chainRenderer : ropeRenderer;
    const inactive = useChain ? ropeRenderer : chainRenderer;
    const activeHandle = useChain
      ? deckAnchorRodeChainHandle
      : deckAnchorRodeRopeHandle;
    const inactiveHandle = useChain
      ? deckAnchorRodeRopeHandle
      : deckAnchorRodeChainHandle;
    inactive.hideLine(inactiveHandle);
    active.updateLine(
      activeHandle,
      a,
      b,
      Math.max(0.2, dist),
      state.waterLevel,
      { clampToWater: false },
    );
  };

  let deployedAnchor: DeployedAnchor | null = null;

  const syncPlayerAnchorBeforeMooring = (): void => {
    const wantsSeabedAnchor =
      state.anchorState === "down" || state.anchorState === "weighing";

    if (!wantsSeabedAnchor) {
      if (deployedAnchor) {
        allAnchors.delete(PLAYER_ANCHOR_ID);
        deployedAnchor = null;
      }
      if (state.mooringTargets.bow === PLAYER_ANCHOR_ID) {
        state.mooringTargets.bow = null;
      }
      if (state.anchorState === "up") {
        state.anchorHoldingState = "stowed";
        state.anchorWorldX = null;
        state.anchorWorldZ = null;
      }
      return;
    }

    if (!deployedAnchor) {
      const dropPoint = anchorRodeExit
        ? bodyPointToWorld3(playerBoat, anchorRodeExit)
        : bodyPointToWorld3(playerBoat, [loa * 0.45, 0.95, 0]);
      const x = state.anchorWorldX ?? dropPoint[0];
      const z = state.anchorWorldZ ?? dropPoint[2];
      deployedAnchor = new DeployedAnchor({
        x,
        z,
        waterLevel: state.waterLevel,
        depth: state.anchorEstimatedDepth,
      });
      allAnchors.set(PLAYER_ANCHOR_ID, deployedAnchor);
      state.anchorWorldX = x;
      state.anchorWorldZ = z;
    }

    state.mooringTargets.bow = PLAYER_ANCHOR_ID;
    state.mooringRestLengths.bow = Math.max(1, state.anchorChainLength);
  };

  const stepPlayerAnchorAfterMooring = (dt: number): void => {
    if (!deployedAnchor) return;
    deployedAnchor.step({
      dt,
      waterLevel: state.waterLevel,
      depth: state.anchorEstimatedDepth,
      chainLength: state.anchorChainLength,
      bowHeightAboveWater: anchorRodeExit?.[1] ?? 0.8,
      rodeWeightKgPerM: playerBoat.params.anchorRodeWeightKgPerM,
    });
    const [x, , z] = deployedAnchor.getWorldPos();
    state.anchorWorldX = x;
    state.anchorWorldZ = z;
    state.anchorHoldingState = deployedAnchor.holdingState;
  };

  // Resolve each moored slot to real anchors now that bollards +
  // buoys + ropeRenderer all exist. Two stern lines per boat →
  // nearest dock-edge bollards on the slot's side; for buoy-style
  // docks one bow line → the nearest free buoy. Reserves its own
  // rope-renderer slots inside.
  mooredBoats.wireMooringLines({
    annotationDocks,
    buoys,
    ropeRenderer,
  });
  // SSAO is the single most expensive non-water pass (~7 ms on a typical
  // discrete GPU). The "low" quality preset skips it entirely and feeds
  // BlitPass a 1×1 white texture so the AO contribution becomes a no-op
  // (`color *= 1.0`). No shader changes required.
  let blitAoView: GPUTextureView;
  if (qualityProfile.ssaoEnabled) {
    const ssaoPass = new SSAOPass(gpu.device, rt, camera, {
      intensity: qualityProfile.ssaoIntensity,
    });
    pipeline.add(ssaoPass);
    blitAoView = ssaoPass.view;
    ssaoPass.onResize((view) => blitPass.setAoView(view));
  } else {
    const whiteTex = gpu.device.createTexture({
      label: "BlitPass.aoFallbackWhite",
      size: { width: 1, height: 1 },
      format: "r8unorm",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    });
    gpu.device.queue.writeTexture(
      { texture: whiteTex },
      new Uint8Array([255]),
      { bytesPerRow: 1 },
      { width: 1, height: 1 },
    );
    blitAoView = whiteTex.createView({
      label: "BlitPass.aoFallbackWhite.view",
    });
  }
  const blitPass = new BlitPass(gpu.device, rt, gpu.canvasFormat, blitAoView);
  pipeline.add(blitPass);

  // Bind ShadowPass's consumer bind group to the terrain & water passes.
  terrainPass.shadowBindGroup = shadowPass.consumerBindGroup;
  waterPass.shadowBindGroup = shadowPass.consumerBindGroup;
  staticMeshPass.shadowBindGroup = shadowPass.consumerBindGroup;
  boatPass.shadowBindGroup = shadowPass.consumerBindGroup;
  // Register depth-only casters so boats and instanced static meshes also
  // appear in the shadow map (otherwise they receive shadow but never cast).
  if (!backgroundPreview) shadowPass.addCaster(boatPass);
  shadowPass.addCaster(staticMeshPass);

  // --- Player fenders (pendulum physics) -----------------------------------
  // Each fender is a separate object hanging from a body-frame "hanger"
  // Empty in the player boat's GLB (`Fender.Hanger.{Bow,Mid,Stern}`), with
  // gravity + a distance constraint to the hanger world position. Slice 2
  // (TODO): soft contact with docks, reaction force on the boat, visible
  // rope. Boats whose GLB lacks these anchors silently skip the system.
  let playerFenders: PlayerFenders | null = null;
  let playerFenderRopeHandles: number[] = [];
  if (!backgroundPreview) {
    const hangers = buildFenderHangersFromAnchors(
      playerBoat.params.namedAnchors,
    );
    if (hangers.length > 0) {
      try {
        const fenderScene = await loadBoatGlbScene("boats/fender.glb", {
          axisUp: "Y",
          axisLength: "Z",
          verbose: true,
        });
        const body = fenderScene.parts.find(
          (p) =>
            p.mesh.indices.length > 0 && /fenderbody|body|fender/i.test(p.name),
        );
        const attach = fenderScene.parts.find((p) => /^attach$/i.test(p.name));
        if (!body) {
          console.warn("[main] fender.glb: no FenderBody-like mesh part found");
        } else if (!attach) {
          console.warn(
            "[main] fender.glb: no Attach Empty found, cannot anchor rope",
          );
        } else {
          const localAttach: [number, number, number] = [
            attach.pivotPosition[0] - body.pivotPosition[0],
            attach.pivotPosition[1] - body.pivotPosition[1],
            attach.pivotPosition[2] - body.pivotPosition[2],
          ];
          let bodyMinY = +Infinity;
          let bodyMaxY = -Infinity;
          for (let i = 1; i < body.verticesPbr.length; i += 11) {
            const y = body.verticesPbr[i]!;
            bodyMinY = Math.min(bodyMinY, y);
            bodyMaxY = Math.max(bodyMaxY, y);
          }
          const bodyHeight = Math.max(0.4, bodyMaxY - bodyMinY);
          const bodyLocalTopY = Math.max(0, bodyMaxY);
          // Blender authoring convention: FenderBody pivot is placed at
          // the Attach point (top of the capsule), so the body's geometry
          // extends downward in local −Y. When the two pivots coincide
          // (offset ≈ 0), fall back to the canonical "up = +Y" axis so the
          // rotation in {@link PlayerFenders} has a well-defined hanging
          // direction. The magnitude is the local vertical reach from the
          // body pivot to the top of the fender mesh, not the whole scene's
          // absolute bbox height.
          const offsetMag = Math.hypot(
            localAttach[0],
            localAttach[1],
            localAttach[2],
          );
          if (offsetMag < 1e-2) {
            // Hang the body downward: shift the rendered body so its top
            // (local +Y extent) coincides with the Attach point. Distance
            // to shift = the vertical reach above the body pivot.
            localAttach[0] = 0;
            localAttach[1] = bodyLocalTopY;
            localAttach[2] = 0;
          }
          const ropeLen = 0.45;
          const bodyRadius = 0.1;
          // Sample a half-beam profile from the player hull mesh so the
          // fender's hull-collision pass uses the actual topsides shape
          // (narrow at bow/stern, wide amidships) rather than an
          // analytic wall. Y band covers the vertical reach of any
          // deployed Attach: hangerY range minus rope+bodyHeight to
          // hangerY+small. xMin/xMax cover the full hull length.
          let topsides: ReturnType<
            typeof buildTopsidesProfileFromScene
          > | null = null;
          const playerScene = playerBoat.params.customScene;
          if (playerScene) {
            let hangerYMin = +Infinity;
            let hangerYMax = -Infinity;
            for (const h of hangers) {
              if (h.bodyPos[1] < hangerYMin) hangerYMin = h.bodyPos[1];
              if (h.bodyPos[1] > hangerYMax) hangerYMax = h.bodyPos[1];
            }
            // Sample half-beam in the Y band around the resting fender
            // body (hangerY - ropeLen, ± bodyHeight/2) rather than the
            // full vertical reach. The hull is typically widest at deck
            // level and narrower a fender-length below, so taking the
            // deck-level beam would leave a visible gap between the
            // fender body and the topsides.
            const restY = (hangerYMin + hangerYMax) * 0.5 - ropeLen;
            const yMin = restY - bodyHeight * 0.5;
            const yMax = restY + bodyHeight * 0.5;
            topsides = buildTopsidesProfileFromScene(playerScene.parts, {
              xMin: playerScene.bbox.min[0],
              xMax: playerScene.bbox.max[0],
              yMin,
              yMax,
              numStations: 64,
            });
          }
          const fenders = new PlayerFenders(
            playerBoat,
            hangers,
            localAttach,
            ropeLen,
            { bodyRadius, topsides },
          );
          playerFenders = fenders;
          for (let i = 0; i < fenders.count; i++) {
            const idx = i;
            const key = fenders.fenderKey(idx);
            boatPass.addFloatingScenePart(
              0,
              body.verticesPbr,
              body.mesh.indices,
              () => fenders.getWorldPose(idx),
              () => fenders.isDeployed(idx),
              `boat0.fender.${key}`,
            );
          }
          // One thin rope per fender — handles parallel `fenders` order.
          playerFenderRopeHandles = [];
          for (let i = 0; i < fenders.count; i++) {
            playerFenderRopeHandles.push(fenderRopeRenderer.addLine());
          }
          console.log(
            `[main] PlayerFenders: ${fenders.count} fenders, ropeLen=${ropeLen}, localAttach=(${localAttach.map((v) => v.toFixed(3)).join(", ")})`,
          );
        }
      } catch (e) {
        console.warn("[main] fender.glb load failed", e);
      }
    } else {
      console.log(
        "[main] PlayerFenders: no Fender.Hanger.* anchors on player boat",
      );
    }
  }

  // --- Boat input + chase camera -------------------------------------------
  const boatInput = new BoatInput(playerBoat);
  // Cockpit eye position: prefer a named Empty `Skipper.Head` (or any
  // common spelling/separator) authored into the source glb; fall back
  // to the per-preset `anchors.cameraCockpit`; final fall back is the
  // hard-coded default inside ChaseCamera.
  const skipperHead = pickNamedAnchor(playerBoat.params.namedAnchors, [
    "Skipper.Head",
    "skipper.head",
    "Skipper_Head",
    "skipper_head",
    "SkipperHead",
    "skipperHead",
    "skipper-head",
    "Helmsman.Head",
    "helmsman.head",
  ]);
  const cockpitOffset =
    skipperHead ??
    (playerBoat.params.anchors?.cameraCockpit
      ? ([
          playerBoat.params.anchors.cameraCockpit[0],
          playerBoat.params.anchors.cameraCockpit[1],
          playerBoat.params.anchors.cameraCockpit[2],
        ] as [number, number, number])
      : undefined);
  if (skipperHead) {
    console.log(
      `[ChaseCamera] cockpit eye from glb anchor "Skipper.Head" → (${skipperHead[0].toFixed(2)}, ${skipperHead[1].toFixed(2)}, ${skipperHead[2].toFixed(2)}) m`,
    );
  }
  const chaseCamera = new ChaseCamera(camera, playerBoat, {
    cockpitOffset,
    syncCockpitOffsetFromBoatAnchors: !skipperHead,
  });
  // Optional preview camera, driven by `flyby_line` items baked into the
  // active terrain. First path wins; rest are reserved for future cycling.
  const flybyCamera = new FlybyCamera(camera);
  const flybyPaths = activeTerrain
    ? collectFlybyPaths(activeTerrain.worldObjects)
    : [];
  if (flybyPaths.length > 0) {
    flybyCamera.setPath(flybyPaths[0]!);
    console.log(
      `[FlybyCamera] loaded ${flybyPaths.length} path(s); active = "${flybyPaths[0]!.id}" (${flybyPaths[0]!.points.length} pts, speed ${flybyPaths[0]!.speedMps} m/s, height ${flybyPaths[0]!.heightM} m)`,
    );
  }
  const waterField = new WaterField();
  const updateBoatHud =
    opts.enableHud === false
      ? (_wx: number, _wz: number) => {}
      : createBoatHud(playerBoat);

  // 1/2/3 keyboard shortcuts for camera mode; "C" recenters helmsman gaze.
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement) return;
    let m: typeof state.cameraMode | null = null;
    if (e.key === "1") m = "external";
    else if (e.key === "2") m = "cockpit";
    else if (e.key === "3") m = "free";
    else if (e.key === "4" && flybyCamera.hasPath()) m = "flyby";
    if (m && state.cameraMode !== m) {
      state.cameraMode = m;
      chaseCamera.setMode(m);
      if (m === "flyby") flybyCamera.reset();
    } else if (
      (e.key === "c" || e.key === "C") &&
      state.cameraMode === "cockpit"
    ) {
      chaseCamera.recenterCockpitView();
    }
  };
  window.addEventListener("keydown", onKeyDown);

  // --- Frame loop ------------------------------------------------------------
  let lastT = performance.now() * 0.001;
  let rafId = 0;
  let running = false;
  // FPS counter (EMA over ~0.5s window, displayed in #fps overlay).
  const fpsEl = document.getElementById("fps");
  let fpsAccum = 0;
  let fpsFrames = 0;
  let fpsLastUpdate = lastT;
  // Frame-time histogram diagnostic. Enable via `window.__dtHist = true` in
  // the browser console; prints a bucketed dt distribution every ~2 s and
  // reports the max spike. Useful for spotting "hitches" hidden by avg FPS.
  const dtBuckets = new Uint32Array(8); // <8, <16, <24, <33, <50, <80, <120, >=120 ms
  let dtMaxMs = 0;
  let dtHistFrames = 0;
  let dtHistLastReport = lastT;
  let cpuSumMs = 0;
  let cpuMaxMs = 0;
  let frameCounter = 0;
  const frame = async () => {
    if (!running) return;
    const now = performance.now() * 0.001;
    const rawDtMs = (now - lastT) * 1000;
    const dt = Math.min(0.05, now - lastT); // clamp to avoid huge steps after tab inactive
    lastT = now;
    frameCounter++;

    // FPS update.
    fpsAccum += dt;
    fpsFrames++;
    if (now - fpsLastUpdate >= 0.5) {
      const fps = fpsFrames / fpsAccum;
      if (fpsEl) fpsEl.textContent = `${fps.toFixed(0)} fps`;
      fpsAccum = 0;
      fpsFrames = 0;
      fpsLastUpdate = now;
    }

    if ((window as unknown as { __dtHist?: boolean }).__dtHist) {
      const ms = rawDtMs;
      if (ms > dtMaxMs) dtMaxMs = ms;
      const b =
        ms < 8
          ? 0
          : ms < 16
            ? 1
            : ms < 24
              ? 2
              : ms < 33
                ? 3
                : ms < 50
                  ? 4
                  : ms < 80
                    ? 5
                    : ms < 120
                      ? 6
                      : 7;
      dtBuckets[b]!++;
      dtHistFrames++;
      if (now - dtHistLastReport >= 2.0) {
        const labels = [
          "<8",
          "<16",
          "<24",
          "<33",
          "<50",
          "<80",
          "<120",
          ">=120",
        ];
        const pct = Array.from(dtBuckets).map(
          (n) => `${((n / dtHistFrames) * 100).toFixed(0)}%`,
        );
        const cpuAvg = cpuSumMs / Math.max(1, dtHistFrames);
        // eslint-disable-next-line no-console
        console.log(
          `[dtHist] ${dtHistFrames}f over 2s · max=${dtMaxMs.toFixed(1)}ms · ` +
            `cpu avg=${cpuAvg.toFixed(1)}ms max=${cpuMaxMs.toFixed(1)}ms · ` +
            labels.map((l, i) => `${l}:${pct[i]}`).join("  "),
        );
        dtBuckets.fill(0);
        dtMaxMs = 0;
        dtHistFrames = 0;
        cpuSumMs = 0;
        cpuMaxMs = 0;
        dtHistLastReport = now;
      }
    }

    // Sync state → uniforms (cheap; UI may have mutated state).
    lighting.state.sunAzimuth = deg2rad(state.sunAzimuthDeg);
    lighting.state.sunElevation = deg2rad(state.sunElevationDeg);
    lighting.upload();

    // Boat sim & chase cam.
    boatInput.step(dt);
    const windRad = (state.windDirectionDeg * Math.PI) / 180;
    const effectiveWindSpeed = state.windSpeed * (state.windExposure ?? 1);
    const windWx = -Math.sin(windRad) * effectiveWindSpeed;
    const windWz = Math.cos(windRad) * effectiveWindSpeed;
    waterField.windDirRad = windRad;
    waterField.windSpeed = effectiveWindSpeed;
    waterField.time += dt;
    // Mooring tension is applied BEFORE the boat & buoy integrate so the
    // forces are consumed in their respective `step` calls this frame.
    syncPlayerAnchorBeforeMooring();
    playerStations.applyMooringPhysics(dt, state);
    stepPlayerAnchorAfterMooring(dt);
    playerBoat.anchorDeployT +=
      (state.anchorDeployT - playerBoat.anchorDeployT) * Math.min(1, dt * 4);
    playerBoat.step(dt, windWx, windWz, waterField, state.waterLevel);
    if (playerFenders) {
      playerFenders.setDeployed(state.fenderDeployed);
      playerFenders.step(dt);
      for (let i = 0; i < playerFenders.count; i++) {
        const handle = playerFenderRopeHandles[i];
        if (handle === undefined) continue;
        if (!playerFenders.isDeployed(i)) {
          fenderRopeRenderer.hideLine(handle);
          continue;
        }
        const ep = playerFenders.getRopeEndpoints(i);
        fenderRopeRenderer.updateLine(
          handle,
          ep.hanger,
          ep.attach,
          playerFenders.ropeLength,
          state.waterLevel,
          { clampToWater: false },
        );
      }
    }
    updateBoatHud(windWx, windWz);
    // Animate tell-tales from apparent wind (true wind minus boat velocity).
    // Throttled to every 3rd frame: tell-tale orientation changes slowly
    // (wind is smooth) and rebuilding the boat mesh every frame was a
    // ~10-15 ms CPU spike contributor.
    if (!backgroundPreview && frameCounter % 3 === 0) {
      boatPass.rebuildMeshForBoatRuntime(0, {
        windX: windWx,
        windZ: windWz,
        time: now,
      });
    }
    if (!backgroundPreview) {
      boatPass.setFenderVisibility(
        0,
        state.fendersPortVisible,
        state.fendersStarboardVisible,
      );
    }
    for (let i = 0; i < aiPilots.length; i++) {
      aiPilots[i]!.step(dt);
      boats[i + 1]!.step(dt, windWx, windWz, waterField, state.waterLevel);
    }
    // Moored boats integrate (so they bob with waves) and then get
    // pinned back to their slip pose so wind/waves can't drift them
    // away. Vertical heave + pitch + roll from buoyancy are kept.
    mooredBoats.stepAndPin(dt, windWx, windWz, waterField, state.waterLevel);
    // Resolve overlaps after all integrations have completed.
    // Reuse the same array each frame to avoid per-frame allocation; main
    // loop drains player events into `pendingImpacts` below.
    frameImpacts.length = 0;
    // Buoys do NOT block the boat — the hull sails right through them.
    // Instead we shove them aside / under the bow wave each frame.
    pushBuoysAwayFromBoats(boats, buoys, buoyLibrary);
    resolveCollisions(collisionBodies, dockAabbs, frameImpacts, dockObbs);
    if (activeTerrain) {
      resolveTerrainShoreCollisions(
        collisionBodies,
        (x, z) => {
          // Treat any classified non-water cell as shore EXCEPT surfaces
          // the hull legitimately occupies in plan-view:
          //  - dock: handled separately by static AABBs in resolveCollisions
          //  - bridge: boats sail under it
          //  - powerline: line is overhead; water below
          const s = sampleTerrainSurface(activeTerrain!, x, z);
          if (s === "water") return false;
          if (s === "dock" || s === "bridge" || s === "powerline") {
            return false;
          }
          // Cross-check with the heightmap: if the bed at this cell is
          // below the water line, the boat can float over it regardless
          // of how the classification raster labelled it. This eliminates
          // phantom shore-hits in shallow water caused by misaligned
          // OSM water polygons vs the terrain mesh / classification cell
          // granularity (~few metres).
          const h = sampleTerrainHeight(activeTerrain!, x, z);
          if (h < state.waterLevel - 0.05) return false;
          return true;
        },
        undefined,
        undefined,
        frameImpacts,
      );
    }
    // Filter to player-boat events. AI boats banging the dock would otherwise
    // produce off-screen "thuds" with no visual cause.
    playerImpactBus.ingestFrame(frameImpacts);
    floatingObjects.step(dt);
    boatSpawner.step(dt, state.waterLevel);
    // Update buoys (each one has its own buoyancy state) and refresh their
    // model matrices so the renderer sees the new translation.
    for (let i = 0; i < buoys.length; i++) {
      buoys[i]!.step(dt, waterField, state.waterLevel);
    }
    buoyMeshRegistry.update();
    // Refresh the rope geometry to the new endpoint positions, then mark
    // the rope group dirty so the GPU buffer is re-uploaded this frame.
    playerStations.updateRopeGeometry(dt, state);
    updateDeckAnchorRodeVisual();
    // Moored boats: render their fixed lines from cleat to dock-edge anchor.
    mooredBoats.updateRopeGeometry(state.waterLevel);
    staticMeshPass.markDirty(ropeRenderer.meshId);
    staticMeshPass.markDirty(chainRenderer.meshId);
    if (playerFenders) staticMeshPass.markDirty(fenderRopeRenderer.meshId);
    if (state.showCollisionFootprint) {
      const layers = getBoatCollisionLayers(playerBoat);
      const profile = layers[0]?.profile;
      if (profile) {
        const outline2D = getBoatCollisionWorldOutline(playerBoat, profile);
        const y = state.waterLevel + 0.02;
        const points: [number, number, number][] = outline2D.map((p) => [
          p.x,
          y,
          p.z,
        ]);
        // Close the loop so the polyline forms a ring.
        if (points.length > 0) points.push(points[0]!);
        collisionFootprintRenderer.updatePolyline(
          collisionFootprintHandle,
          points,
        );
      } else {
        collisionFootprintRenderer.hideLine(collisionFootprintHandle);
      }
      staticMeshPass.markDirty(collisionFootprintRenderer.meshId);
    } else {
      collisionFootprintRenderer.hideLine(collisionFootprintHandle);
      staticMeshPass.markDirty(collisionFootprintRenderer.meshId);
    }
    setColliderWireframesVisible(state.showStaticColliders);
    if (state.showWindStreaks) {
      windStreakSpawner.step(
        dt,
        windRad,
        state.windSpeed,
        state.waterLevel,
        playerBoat.posX,
        playerBoat.posZ,
      );
    }
    if (state.showBirds) {
      birdsSpawner.step(dt, state.waterLevel, playerBoat.posX, playerBoat.posZ);
    } else {
      birdsSpawner.clear();
    }
    particleSystem.step(dt, 9.81, 0.6);
    chaseCamera.step(dt, state.waterLevel);
    if (state.cameraMode === "flyby") {
      flybyCamera.step(dt, state.waterLevel);
    }

    // Keep the sun's shadow ortho centred on the player boat so the boat
    // stays inside the (small, tight) shadow frustum and actually casts a
    // visible shadow on the surrounding water.
    shadow.centerXZ[0] = playerBoat.posX;
    shadow.centerXZ[1] = playerBoat.posZ;

    const swapTex = gpu.context.getCurrentTexture();
    const frameCtx: FrameContext = {
      device: gpu.device,
      renderTarget: rt.current,
      swapchainView: swapTex.createView({ label: "swapchain view" }),
      swapchainFormat: gpu.canvasFormat,
      camera,
      lighting,
      palette,
      state,
      time: now,
      dt,
      width: gpu.size.width,
      height: gpu.size.height,
    };

    // DEV-only per-pass GPU/CPU profiler. Trigger from the browser console
    // with `__perfNext = true` or `__perfRuns = N`. Each pass is submitted in
    // its own command buffer with a fence after, so wall time between fences
    // approximates the GPU cost of that pass (plus one round-trip of CPU↔GPU
    // sync overhead, which is uniform across passes and easy to spot).
    const w = window as unknown as {
      __perfNext?: boolean;
      __perfRuns?: number;
      __perfLast?: Array<{ name: string; ms: number; cpuMs: number }>;
    };
    const runProfile = w.__perfNext === true || (w.__perfRuns ?? 0) > 0;
    if (runProfile) {
      w.__perfNext = false;
      if ((w.__perfRuns ?? 0) > 0) w.__perfRuns = (w.__perfRuns ?? 0) - 1;
      const results: Array<{ name: string; ms: number; cpuMs: number }> = [];
      for (const pass of pipeline.passes) {
        // BlitPass writes to the swapchain texture which is only valid
        // within one rAF; splitting submits invalidates it before Blit
        // and triggers "Destroyed texture ... used in a submit". Skip it.
        if (pass.name === "BlitPass") continue;
        const enc = gpu.device.createCommandEncoder({ label: pass.name });
        const c0 = performance.now();
        pass.encode(enc, frameCtx);
        const cpuMs = performance.now() - c0;
        const t0 = performance.now();
        gpu.device.queue.submit([enc.finish()]);
        // eslint-disable-next-line no-await-in-loop
        await gpu.device.queue.onSubmittedWorkDone();
        results.push({
          name: pass.name,
          ms: performance.now() - t0,
          cpuMs,
        });
      }
      // Finalize the frame with a real BlitPass so the canvas still updates
      // and the swapchain texture isn't leaked.
      const blit = pipeline.passes.find((p) => p.name === "BlitPass");
      if (blit) {
        const enc = gpu.device.createCommandEncoder({
          label: "BlitPass(final)",
        });
        blit.encode(enc, frameCtx);
        gpu.device.queue.submit([enc.finish()]);
      }
      results.sort((a, b) => b.ms - a.ms);
      w.__perfLast = results;
      // eslint-disable-next-line no-console
      console.table(results);
    } else {
      const encoder = gpu.device.createCommandEncoder({
        label: "frame encoder",
      });
      pipeline.encode(encoder, frameCtx);
      gpu.device.queue.submit([encoder.finish()]);
    }

    if ((window as unknown as { __dtHist?: boolean }).__dtHist) {
      const cpuMs = performance.now() - now * 1000;
      cpuSumMs += cpuMs;
      if (cpuMs > cpuMaxMs) cpuMaxMs = cpuMs;
    }

    rafId = requestAnimationFrame(frame);
  };
  running = true;
  rafId = requestAnimationFrame(frame);

  let disposed = false;
  return {
    state,
    stop() {
      running = false;
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    },
    resume() {
      if (disposed || running) return;
      running = true;
      lastT = performance.now() * 0.001;
      rafId = requestAnimationFrame(frame);
    },
    setState(patch) {
      // Camera mode lives on `state` but also drives ChaseCamera; forward
      // the change before the bare assign so save/restore inside setMode
      // sees the previous mode.
      if (patch.cameraMode && patch.cameraMode !== state.cameraMode) {
        chaseCamera.setMode(patch.cameraMode);
        if (patch.cameraMode === "flyby") flybyCamera.reset();
      }
      Object.assign(state, patch);
    },
    listBoatChoices() {
      return boatLibrary.list().map((e) => ({ id: e.id, name: e.name }));
    },
    setBoatControls(patch) {
      if (patch.throttle !== undefined) {
        boatInput.setThrottleOverride(patch.throttle);
      }
      if (patch.gas !== undefined) {
        boatInput.setGasOverride(patch.gas);
      }
      if (patch.gear !== undefined) {
        boatInput.setGearOverride(patch.gear);
      }
      if (patch.steer !== undefined) {
        boatInput.setSteerOverride(patch.steer);
      }
      if (patch.engineSteer !== undefined) {
        boatInput.setEngineSteerOverride(patch.engineSteer);
      }
    },
    boatTelemetry() {
      return computeBoatTelemetry(playerBoat, state);
    },
    consumeImpactEvents() {
      return playerImpactBus.consume();
    },
    getMooringPickupCandidate(cleatName) {
      return pickupService.findCandidate(cleatName);
    },
    pickAnchorAtNdc(ndcX, ndcY) {
      return anchorPicker.pickAtNdc(ndcX, ndcY);
    },
    getFairleadAnchorDistance(cleatName) {
      return playerStations.getFairleadAnchorDistance(cleatName);
    },
    getPlayerFreeboard() {
      return playerBoat.params.freeboard ?? 0.6;
    },
    getPlayerAnchorEquipment() {
      return {
        hasDepthSounder: playerBoat.params.hasDepthSounder,
        anchorChainMaxLength: playerBoat.params.anchorChainMaxLength,
        anchorRodeType: playerBoat.params.anchorRodeType,
        anchorRodeMaxLength: playerBoat.params.anchorRodeMaxLength,
        anchorRodeWeightKgPerM: playerBoat.params.anchorRodeWeightKgPerM,
      };
    },
    getPlayerHasFoldableMast() {
      return playerBoat.params.foldableMast;
    },
    setPlayerMastAngleTarget(angleRad) {
      if (!playerBoat.params.foldableMast) return;
      const clamped = Math.max(0, Math.min(Math.PI / 2, angleRad));
      playerBoat.mastAngleTarget = clamped;
    },
    setPlayerBoomDetached(detached) {
      if (!playerBoat.params.foldableMast) return;
      playerBoat.boomDetached = detached;
    },
    listTerrainPresets() {
      return terrainPresets.map((p) => ({ id: p.id, name: p.name }));
    },
    releaseBirds() {
      birdsSpawner.releaseNow(
        state.waterLevel,
        playerBoat.posX,
        playerBoat.posZ,
      );
    },
    spawnBuoy(kind, posX, posZ, opts) {
      return spawnBuoy({
        kind,
        posX,
        posZ,
        id: opts?.id ?? `runtime_buoy_${kind}_${Date.now()}_${buoys.length}`,
        anchored: opts?.anchored,
        headingY: opts?.headingY,
      });
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      running = false;
      if (rafId !== 0) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      window.removeEventListener("keydown", onKeyDown);
      gpu.dispose();
    },
    // DEV-only escape hatch for browser-driven manual tests. Exposes
    // raw simulation objects so the test harness can teleport the
    // boat, list buoys/bollards, etc. Stripped in prod by Vite's
    // dead-code elimination.
    _devInternals: import.meta.env.DEV
      ? {
          playerBoat,
          buoys,
          annotationDocks,
          mainDock: dock,
          chaseCamera,
          camera,
        }
      : undefined,
  };
}

/**
 * Object-oriented lifecycle wrapper around {@link startApp}. Friendlier to
 * embed in framework components (Angular, React, Web Components): construct
 * with a canvas, call `start()` once it's mounted, `dispose()` on teardown.
 */
export class CastOffProApp {
  private handle: AppHandle | null = null;
  private startPromise: Promise<AppHandle> | null = null;
  private disposed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly opts: AppOpts = {},
  ) {}

  /** Boot the app. Idempotent: returns the same promise on repeat calls. */
  start(): Promise<AppHandle> {
    if (this.disposed) {
      return Promise.reject(new Error("CastOffProApp has been disposed"));
    }
    if (this.startPromise) return this.startPromise;
    this.startPromise = startApp(this.canvas, this.opts).then((h) => {
      this.handle = h;
      // If `dispose()` was called while we were booting, tear down now.
      if (this.disposed) h.dispose();
      return h;
    });
    return this.startPromise;
  }

  stop(): void {
    this.handle?.stop();
  }

  resume(): void {
    this.handle?.resume();
  }

  setState(patch: Partial<AppState>): void {
    this.handle?.setState(patch);
  }

  setBoatControls(patch: {
    throttle?: number | null;
    gas?: number | null;
    gear?: -1 | 0 | 1 | null;
    steer?: number | null;
    engineSteer?: number | null;
  }): void {
    this.handle?.setBoatControls(patch);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.handle?.dispose();
  }

  /** Live state reference (only valid after `start()` resolves). */
  get state(): AppState | null {
    return this.handle?.state ?? null;
  }
}

// --- Dev entry: auto-bootstrap when index.html is served directly ----------
const devCanvas = document.getElementById(
  "gpu-canvas",
) as HTMLCanvasElement | null;
if (devCanvas) {
  startApp(devCanvas).catch((err) => {
    console.error(err);
    showError(String(err && (err as Error).stack ? (err as Error).stack : err));
  });
}
